import {
  Accordion,
  AccordionContent as RawAccordionContent,
  AccordionItem as RawAccordionItem,
  AccordionTrigger as RawAccordionTrigger,
} from "@/components/ui/accordion";

// Untyped .jsx forwardRef components — cast to `any` so TSX accepts children.
const AccordionItem = RawAccordionItem as any;
const AccordionTrigger = RawAccordionTrigger as any;
const AccordionContent = RawAccordionContent as any;

const FAQ = [
  {
    q: "What happens after I join?",
    a: "Once your enrollment is complete, our onboarding team will contact you to begin the setup process.\n\nWe'll guide you through onboarding, collect the information we need, and prepare your account so your Done-for-You services can begin.\n\nYour marketing team will then start preparing your content, lead generation, and growth strategy based on the plan you selected.",
  },
  {
    q: "When does my service begin?",
    a: "Your onboarding starts immediately after joining.\n\nYour Done-for-You services begin within 10–14 business days, allowing our team enough time to complete onboarding, prepare your brand assets, train your assigned specialists, and build your workflow for long-term success.",
  },
  {
    q: "What is the difference between PREMIUM and ELITE?",
    a: "Both plans include Done-for-You marketing, lead generation, qualified leads, and monthly growth support.\n\nThe difference is simple.\n\nPREMIUM\n• You conduct your own Discovery Calls.\n• You present the WinCampaign opportunity to your prospects.\n\nELITE\n• A dedicated WinCampaign Growth Advisor conducts up to 10 Discovery Calls per month on your behalf, professionally presenting the opportunity and helping convert qualified prospects into subscribers.",
  },
  {
    q: "What is my role as a Brand Affiliate?",
    a: "WinCampaign helps you grow your business — but you remain the owner of your relationships.\n\nYour role includes:\n• Replying to inquiries from qualified leads\n• Building relationships and trust\n• Nurturing prospects before and after Discovery Calls\n• Welcoming and supporting your new subscribers\n\nOur team handles the marketing, lead generation, and (for ELITE members) Discovery Calls while you continue building your recurring income business.",
  },
  {
    q: "How does lead generation work?",
    a: "Our Growth Specialists research qualified prospects, build targeted outreach campaigns, and consistently generate new opportunities for your business.\n\nQualified leads are delivered directly to you so you can begin conversations and build relationships.\n\nFor ELITE members, once a prospect is ready, your dedicated Growth Advisor conducts the Discovery Call on your behalf.",
  },
  {
    q: "Can I cancel or pause my membership?",
    a: "Your options depend on the payment plan you choose.\n\nMonthly Plan\n• Cancel anytime before your next billing date.\n\n5 Easy Payment Plan\n• Five-payment commitment required.\n• Includes one (1) 30-day pause during the payment term.\n• During the pause, your Done-for-You Business Growth Accelerator services will also be paused, including content creation, lead generation, and Discovery Call services.\n• Monthly payments and services will automatically resume the following month until all five (5) installments have been completed.\n• The 30-day pause is intended for temporary circumstances and may only be used once during the payment term.\n• The 5 Easy Payment Plan is a payment commitment and cannot be canceled before all five installments have been completed.\n\nAnnual Plan\n• Annual memberships are prepaid and cannot be canceled once purchased.",
  },
  {
    q: "Do you offer refunds?",
    a: "Because we begin allocating resources, onboarding, training, and preparing your dedicated marketing team immediately after you join, all sales are final and non-refundable.\n\nThis allows us to commit the necessary time and resources to delivering your Done-for-You services from the moment you become a Brand Affiliate.",
  },
  {
    q: "Will this guarantee I earn $10,000 per month?",
    a: "No.\n\nYour results depend on many factors, including your consistency, follow-up, relationship building, and the actions you take as a Brand Affiliate.\n\nWinCampaign provides the systems, marketing, lead generation, and support designed to help you build a recurring income business, but individual results will always vary.",
  },
];

export default function FAQ_Section() {
  return (
    <section
      id="faq"
      data-testid="wc-faq"
      className="bg-white py-20 sm:py-28"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-[#f3efff] text-[#673dfc] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
            FAQ
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#30265b] leading-[1.1]">
            Still have questions? We give you clarity.
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Let&apos;s make sure you feel confident before we build your business together.
          </p>
        </div>

        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {FAQ.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              data-testid={`wc-faq-item-${i}`}
              className="border border-gray-200 rounded-2xl bg-white px-5 sm:px-6 data-[state=open]:border-[#c5b4ff] data-[state=open]:shadow-sm transition-colors"
            >
              <AccordionTrigger className="font-display font-semibold text-left text-[#30265b] text-base sm:text-lg py-5 hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-5 text-sm sm:text-base whitespace-pre-line">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
